/**
 * Templates — hybrid classification pipeline (browser-side).
 *
 * Input: a Canva-exported SVG file. Output (in three stages):
 *   1. detectImageCandidates — pure DOM detection of every <image> element,
 *      with normalized bbox + rotation + clip-path + thumbnail. The admin
 *      reviews these and marks which are SLOTs (photo placeholders) vs
 *      DECOR (baked-in graphics).
 *   2. buildStrippedOverlaySvg — at save time, removes the confirmed SLOT
 *      <image> elements from the original SVG. Everything else (decor,
 *      backgrounds, frames) stays so the overlay preserves the template's
 *      visual identity.
 *   3. buildSlotsJson — serializes the admin's confirmed SLOTs into the
 *      row shape stored in the layout_template_slots table.
 *
 * The legacy `processSvgTemplate` and its 8 layers of strip heuristics
 * (page-fill rect/path detection, containment clustering, clip-path size
 * filters, etc.) were deleted in Task 8 — they tried to recover information
 * Canva destroyed at SVG export time, and never worked reliably.
 *
 * Uses browser DOMParser + native `getCTM()`. Zero new deps.
 */

export interface Candidate {
  id: string;
  /** Position of this <image> in the SVG's `querySelectorAll('image')` order.
   *  This is the LOAD-BEARING field for save-time stripping — the `id` is
   *  metadata only. We use index instead of id because:
   *    - Canva SVGs don't ship with `id=` on <image> elements
   *    - Writing auto-ids back via XMLSerializer is fragile across browsers
   *    - Document order is stable across re-parses
   *  As long as the SVG isn't mutated between detect and save, index-based
   *  removal is bulletproof. */
  domIndex: number;
  bbox: { x: number; y: number; w: number; h: number }; // normalized 0..1 vs page
  rotation: number; // degrees
  clipPath: string | null; // SVG path-data string, normalized 0..1 of the slot bbox; null if no clip
  thumbnailDataUrl: string; // 256px-max-edge JPEG q70
}

/**
 * A page-background element (rect or path) detected during candidate
 * detection. Its `domIndex` is the position within the SVG's
 * `querySelectorAll(tag)` collection — used at strip time to remove the
 * element from the overlay SVG before rasterization, so the cream / paper
 * page-fill Canva bakes into the SVG doesn't hide the customer's photos.
 *
 * `fillColor` is the element's resolved fill, normalized to `#rrggbb` when
 * possible. We persist the first non-null hit as the template row's
 * `background_color` so the customer page background matches the template's
 * native page color.
 */
export interface PageBackgroundHit {
  tag: 'rect' | 'path';
  /** Index within the original document's `querySelectorAll(tag)` collection.
   *  Stable across re-parses, like Candidate.domIndex. */
  domIndex: number;
  /** Resolved fill color (`#rrggbb`) or the raw string when we can't
   *  normalize. null when fill is missing or not a color (e.g. a url()). */
  fillColor: string | null;
}

/**
 * Is `el` inside a non-rendered SVG container? Elements inside <defs>,
 * <clipPath>, <mask> or <symbol> aren't rendered on the page, so they
 * shouldn't be considered page-backgrounds. Their indices in the
 * top-level querySelectorAll collection would otherwise shift the
 * strip-time index mapping.
 */
function isInsideNonRenderedAncestor(el: Element): boolean {
  let cur: Element | null = el.parentElement;
  while (cur) {
    const tag = cur.tagName.toLowerCase();
    if (tag === 'defs' || tag === 'clippath' || tag === 'mask' || tag === 'symbol') {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
}

/**
 * Remove the <image> elements at `confirmedSlotIndices` from `svgString`,
 * and optionally also remove page-background `<rect>` / `<path>` elements
 * identified during candidate detection.
 *
 * Why indices not ids: see the JSDoc on `Candidate.domIndex` above.
 *
 * Page-background stripping is the way we keep the customer's page color
 * from being hidden by the overlay PNG. We strip on the SVG side (rather
 * than post-rasterization with destination-out) so decorations that
 * overlap a slot bbox are preserved naturally — they end up sitting on
 * top of the customer's photo at render time via the overlay's z-index.
 *
 * If `confirmedSlotIndices.length > 0` but ZERO slot elements are matched,
 * this throws — that's a fatal bug (slot count mismatch between detect and
 * save) and we'd rather fail loud than silently ship an overlay PNG with
 * the Canva stock photos baked in. Page-background removal failures are
 * NOT fatal — they're a best-effort safety strip and the overlay still
 * works if zero get removed.
 */
export function buildStrippedOverlaySvg(
  svgString: string,
  confirmedSlotIndices: number[],
  pageBackgrounds: PageBackgroundHit[] = [],
): string {
  if (confirmedSlotIndices.length === 0 && pageBackgrounds.length === 0) {
    return svgString;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');

  // Strip slot <image> elements first.
  const images = Array.from(doc.querySelectorAll('image'));
  const indexSet = new Set(confirmedSlotIndices);
  let removed = 0;
  for (let i = 0; i < images.length; i++) {
    if (indexSet.has(i)) {
      images[i].parentNode?.removeChild(images[i]);
      removed++;
    }
  }

  if (confirmedSlotIndices.length > 0 && removed === 0) {
    throw new Error(
      `buildStrippedOverlaySvg: 0/${confirmedSlotIndices.length} slots removed. SVG has ${images.length} <image> elements; requested indices ${JSON.stringify(confirmedSlotIndices)}. This means the SVG was mutated between detect and save — fail loud rather than ship an overlay PNG with stock photos baked in.`,
    );
  }
  if (confirmedSlotIndices.length > 0 && removed < confirmedSlotIndices.length) {
    console.warn(
      `buildStrippedOverlaySvg: removed ${removed}/${confirmedSlotIndices.length} slots — some indices were out of range. SVG has ${images.length} <image> elements.`,
    );
  }

  // Strip page-background rects / paths. Snapshot per tag, then remove by
  // the indices captured at detection time. Defensive: skip elements inside
  // non-rendered containers (defs/clipPath/mask/symbol) so the index
  // mapping matches what detection saw.
  if (pageBackgrounds.length > 0) {
    const rects = Array.from(doc.querySelectorAll('rect'));
    const paths = Array.from(doc.querySelectorAll('path'));
    const rectIndexSet = new Set<number>();
    const pathIndexSet = new Set<number>();
    for (const hit of pageBackgrounds) {
      if (hit.tag === 'rect') rectIndexSet.add(hit.domIndex);
      else if (hit.tag === 'path') pathIndexSet.add(hit.domIndex);
    }
    for (let i = 0; i < rects.length; i++) {
      if (rectIndexSet.has(i) && !isInsideNonRenderedAncestor(rects[i])) {
        rects[i].parentNode?.removeChild(rects[i]);
      }
    }
    for (let i = 0; i < paths.length; i++) {
      if (pathIndexSet.has(i) && !isInsideNonRenderedAncestor(paths[i])) {
        paths[i].parentNode?.removeChild(paths[i]);
      }
    }
  }

  return new XMLSerializer().serializeToString(doc);
}

export function buildSlotsJson(
  confirmedSlotIds: string[],
  candidates: Candidate[]
): Array<{ x: number; y: number; width: number; height: number; rotation: number; clip_path: string | null; dom_index: number }> {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const slots: Array<{ x: number; y: number; width: number; height: number; rotation: number; clip_path: string | null; dom_index: number }> = [];
  for (const id of confirmedSlotIds) {
    const c = byId.get(id);
    if (!c) continue;
    slots.push({
      x: c.bbox.x,
      y: c.bbox.y,
      width: c.bbox.w,
      height: c.bbox.h,
      rotation: c.rotation,
      clip_path: c.clipPath,
      dom_index: c.domIndex,
    });
  }
  return slots;
}

/**
 * 1×1 transparent PNG data URL — 95 bytes. Replaces the embedded Canva
 * placeholder photo's base64 payload (typically 2–10 MB per slot) in the
 * SVG we persist to R2. We never display these placeholders at customer
 * time — empty slots get `opacity=0` and filled slots get their href
 * swapped to the customer's photo via `applyPhotosToTemplateSvg`. So the
 * placeholder payload is pure storage waste.
 *
 * A 5-slot Canva template typically drops from ~40 MB → ~3 MB after
 * slimming, with no visual difference.
 */
const TRANSPARENT_PIXEL_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 * Replace the embedded base64 photo data in each slot's <image> element
 * with a 1×1 transparent PNG before persisting the SVG to R2. The slot's
 * geometry (x/y/width/height, clipPath, transforms) is preserved verbatim
 * — only the href payload is swapped.
 *
 * Pure function. Caller passes the dom_indices of slots confirmed by the
 * admin. Indexed elements not found are silently ignored (allows the
 * caller to be loose about which indices it sends).
 */
export function slimSvgForPersistence(
  svgString: string,
  slotDomIndices: number[],
  deleteDomIndices: number[] = [],
): string {
  if (slotDomIndices.length === 0 && deleteDomIndices.length === 0) return svgString;
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const images = Array.from(doc.querySelectorAll('image'));
  const slotSet = new Set(slotDomIndices);
  const delSet = new Set(deleteDomIndices);
  for (let i = 0; i < images.length; i++) {
    if (delSet.has(i)) {
      // DELETE — remove the element entirely so it renders neither as a photo
      // slot nor as decoration (admin marked it "Supprimer").
      images[i].parentNode?.removeChild(images[i]);
      continue;
    }
    if (!slotSet.has(i)) continue;
    // SLOT — blank the Canva placeholder; the customer's photo href-swaps in.
    images[i].setAttribute('href', TRANSPARENT_PIXEL_DATA_URL);
    images[i].removeAttribute('xlink:href');
  }
  return new XMLSerializer().serializeToString(doc);
}

export interface DetectImageCandidatesResult {
  /** Detected candidates. The `domIndex` on each candidate is the load-bearing
   *  field for save-time stripping — see Candidate's JSDoc. */
  candidates: Candidate[];
  /** Page-background elements (rect/path that covers ≥80% of the page area
   *  with a non-'none' fill). Stripped from the SVG at save time so the
   *  cream page-fill doesn't hide customer photos. */
  pageBackgrounds: PageBackgroundHit[];
  /** First non-null fill color from `pageBackgrounds` — used as the
   *  template's `background_color` when creating the row. null if no page
   *  background was detected (or none had a resolvable fill). */
  pageBackgroundColor: string | null;
}

export async function detectImageCandidates(svgString: string): Promise<DetectImageCandidatesResult> {
  // Parse and mount the SVG off-screen so getCTM works.
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgRoot = doc.documentElement as unknown as SVGSVGElement;

  // Mount with an EXPLICIT pixel size so the SVG actually lays out at a
  // known resolution. Without a fixed size on the host, getBoundingClientRect
  // sometimes returns identity / collapsed values depending on browser, and
  // CTM math drifts from what the browser ultimately renders. We pick a
  // page-equivalent size (the SVG's natural viewBox) so 1 viewBox unit = 1 px.
  const host = document.createElement('div');
  host.style.position = 'absolute';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = `${svgRoot.viewBox.baseVal.width || svgRoot.width.baseVal.value || 1240}px`;
  host.style.height = `${svgRoot.viewBox.baseVal.height || svgRoot.height.baseVal.value || 1754}px`;
  svgRoot.setAttribute('width', '100%');
  svgRoot.setAttribute('height', '100%');
  host.appendChild(svgRoot);
  document.body.appendChild(host);

  // Force synchronous layout — otherwise getBoundingClientRect can return
  // pre-layout values (especially in Safari).
  void host.getBoundingClientRect();

  try {
    const pageW = svgRoot.viewBox.baseVal.width || svgRoot.width.baseVal.value;
    const pageH = svgRoot.viewBox.baseVal.height || svgRoot.height.baseVal.value;
    const pageArea = pageW * pageH;

    // Capture the SVG root's CSS bbox so we can convert browser pixel coords
    // back into viewBox coords. Because we mounted host at viewBox-equivalent
    // pixel size, this should be very close to viewBox dims, but rounding +
    // CSS-units conversion can still introduce sub-pixel drift.
    const rootRect = svgRoot.getBoundingClientRect();
    const pxToVbX = rootRect.width > 0 ? pageW / rootRect.width : 1;
    const pxToVbY = rootRect.height > 0 ? pageH / rootRect.height : 1;

    const images = Array.from(svgRoot.querySelectorAll('image')) as SVGImageElement[];
    const candidates: Candidate[] = [];

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      // Keep auto-id assignment as metadata (used by the admin UI for stable
      // React keys + Gemini's id legend). Stripping uses domIndex, not id —
      // so it doesn't matter if the id round-trips through XMLSerializer.
      if (!img.id) img.id = `img_auto_${i}`;

      // Prefer getBoundingClientRect-derived bbox: it reflects the browser's
      // ACTUAL rendered position after every transform / nested viewBox /
      // CSS-style. CTM-based math drifts on SVGs with non-trivial transform
      // pivots, which made our drop-zone overlay misalign with the
      // visible Canva frame. Fall back to CTM-based math if getBCR returns
      // a degenerate (zero-area) rect.
      const elRect = img.getBoundingClientRect();
      let minX: number, maxX: number, minY: number, maxY: number;
      if (elRect.width > 0 && elRect.height > 0) {
        const localX = (elRect.left - rootRect.left) * pxToVbX;
        const localY = (elRect.top - rootRect.top) * pxToVbY;
        const localW = elRect.width * pxToVbX;
        const localH = elRect.height * pxToVbY;
        minX = Math.max(0, localX);
        maxX = Math.min(pageW, localX + localW);
        minY = Math.max(0, localY);
        maxY = Math.min(pageH, localY + localH);
      } else {
        // Fallback: CTM-based corner transformation (legacy path).
        const ctm = img.getCTM();
        const x = img.x.baseVal.value;
        const y = img.y.baseVal.value;
        const w = img.width.baseVal.value;
        const h = img.height.baseVal.value;
        const corners = [
          { x, y },
          { x: x + w, y },
          { x, y: y + h },
          { x: x + w, y: y + h },
        ].map((p) => (ctm ? { x: ctm.a * p.x + ctm.c * p.y + ctm.e, y: ctm.b * p.x + ctm.d * p.y + ctm.f } : p));
        minX = Math.max(0, Math.min(...corners.map((c) => c.x)));
        maxX = Math.min(pageW, Math.max(...corners.map((c) => c.x)));
        minY = Math.max(0, Math.min(...corners.map((c) => c.y)));
        maxY = Math.min(pageH, Math.max(...corners.map((c) => c.y)));
      }

      const visibleArea = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
      const visibleFrac = visibleArea / pageArea;

      // Visible-area gate: 1.5% to 75% of page. This gate has never been the
      // source of failure — keep it.
      if (visibleFrac < 0.015 || visibleFrac > 0.75) continue;

      // Rotation from CTM: atan2(b, a) in degrees. CTM is still the source
      // of truth for rotation; getBoundingClientRect returns the
      // axis-aligned bbox and discards rotation, so we read CTM here
      // explicitly. For images inside non-rendered ancestors or without
      // a CTM, fall back to 0 — which is correct for non-rotated slots.
      const rotCtm = img.getCTM();
      const rotation = rotCtm ? (Math.atan2(rotCtm.b, rotCtm.a) * 180) / Math.PI : 0;

      // Slot bbox in VIEWPORT space (pre-page-normalization). We need this to
      // normalize the clip-path's coords to 0..1 of the slot bbox before we
      // shrink the bbox to page-relative 0..1.
      const slotVpX = minX;
      const slotVpY = minY;
      const slotVpW = Math.max(0, maxX - minX);
      const slotVpH = Math.max(0, maxY - minY);

      // Clip-path lookup: resolve url(#id) → <clipPath><path d="…"/></clipPath>
      // or <clipPath><rect …/></clipPath>. The clip's coords are in SVG
      // viewport space (userSpaceOnUse, Canva's default) — we normalize them
      // to 0..1 of the slot bbox so the downstream renderer (pdfGenerator
      // .scalePath) can multiply by the slot's pixel size.
      let clipPath: string | null = null;
      const clipAttr = (img.parentElement?.getAttribute('clip-path') ?? '').match(/#([^)]+)/);
      if (clipAttr && slotVpW > 0 && slotVpH > 0) {
        const cp = svgRoot.querySelector(
          `clipPath#${CSS.escape(clipAttr[1])} path, clipPath#${CSS.escape(clipAttr[1])} rect`
        ) as SVGGraphicsElement | null;
        if (cp) {
          // Build a viewport-space d-string regardless of <path> vs <rect>.
          let rawD: string | null = null;
          if (cp.tagName === 'path') {
            rawD = cp.getAttribute('d');
          } else if (cp.tagName === 'rect') {
            const rx = parseFloat(cp.getAttribute('x') ?? '0');
            const ry = parseFloat(cp.getAttribute('y') ?? '0');
            const rw = parseFloat(cp.getAttribute('width') ?? '0');
            const rh = parseFloat(cp.getAttribute('height') ?? '0');
            if (Number.isFinite(rx) && Number.isFinite(ry) && rw > 0 && rh > 0) {
              rawD = `M${rx},${ry} L${rx + rw},${ry} L${rx + rw},${ry + rh} L${rx},${ry + rh} Z`;
            }
          }
          if (rawD) {
            // Canva exports usually leave clipPathUnits as the default
            // userSpaceOnUse and don't apply a transform inside the
            // <clipPath>. Read the CTM defensively in case they do.
            const cpCtm = cp.getCTM();
            clipPath = normalizeClipPathToSlot(rawD, cpCtm, slotVpX, slotVpY, slotVpW, slotVpH, cp);
          }
        }
      }

      const thumbnailDataUrl = await rasterizeImageElement(img, 256);

      candidates.push({
        id: img.id,
        domIndex: i,
        bbox: { x: minX / pageW, y: minY / pageH, w: (maxX - minX) / pageW, h: (maxY - minY) / pageH },
        rotation,
        clipPath,
        thumbnailDataUrl,
      });
    }

    // Page-background detection. We scan rects + paths separately because
    // strip-time removal needs index-within-tag (querySelectorAll('rect') vs
    // querySelectorAll('path')). Elements inside non-rendered containers
    // (defs/clipPath/mask/symbol) are skipped — their indices in the
    // top-level collection still count (they're querySelectorAll matches),
    // but they can't be page-backgrounds because they're not rendered.
    const pageBackgrounds: PageBackgroundHit[] = [];
    let pageBackgroundColor: string | null = null;
    const PAGE_BG_AREA_THRESHOLD = 0.8 * pageArea;

    const scanForPageBg = (tag: 'rect' | 'path') => {
      const els = Array.from(svgRoot.querySelectorAll(tag)) as SVGGraphicsElement[];
      for (let idx = 0; idx < els.length; idx++) {
        const el = els[idx];
        if (isInsideNonRenderedAncestor(el)) continue;
        const fillAttr = el.getAttribute('fill');
        // Skip explicit 'none' fills — they're not visible page-fills.
        if (fillAttr === 'none') continue;
        // Skip url() fills (gradients/patterns) — can't normalize to a color.
        if (fillAttr && /^url\(/i.test(fillAttr.trim())) continue;
        let bbox: { x: number; y: number; width: number; height: number };
        try {
          bbox = el.getBBox();
        } catch {
          continue;
        }
        if (!bbox || bbox.width <= 0 || bbox.height <= 0) continue;
        const area = bbox.width * bbox.height;
        if (area < PAGE_BG_AREA_THRESHOLD) continue;
        // Element qualifies. Missing `fill` attribute defaults to black per
        // SVG spec; normalize whatever color we have.
        const fillColor = normalizeFillColor(fillAttr ?? '#000000');
        pageBackgrounds.push({ tag, domIndex: idx, fillColor });
        if (!pageBackgroundColor && fillColor) {
          pageBackgroundColor = fillColor;
        }
      }
    };

    scanForPageBg('rect');
    scanForPageBg('path');

    return { candidates, pageBackgrounds, pageBackgroundColor };
  } finally {
    document.body.removeChild(host);
  }
}

/**
 * Normalize an SVG fill attribute to `#rrggbb`. Handles `#rgb`, `#rrggbb`,
 * `#rrggbbaa` (alpha discarded), and named colors via the browser's CSS
 * resolver. If we can't normalize, returns the raw string — downstream
 * code can choose to ignore or fall back to a default.
 *
 * Exported only via the detection result; not part of the module API.
 */
function normalizeFillColor(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  // #rgb / #rrggbb / #rrggbbaa
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    const r = s[1], g = s[2], b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(s)) return s.slice(0, 7).toLowerCase();
  // Named color or rgb()/rgba() — let the browser resolve it.
  try {
    const probe = document.createElement('div');
    probe.style.color = s;
    document.body.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    document.body.removeChild(probe);
    // computed is like "rgb(r, g, b)" or "rgba(r, g, b, a)"
    const m = computed.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) {
      const r = parseInt(m[1], 10).toString(16).padStart(2, '0');
      const g = parseInt(m[2], 10).toString(16).padStart(2, '0');
      const b = parseInt(m[3], 10).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`.toLowerCase();
    }
  } catch {
    /* fall through to raw return */
  }
  return s;
}

/**
 * Whitelist of SVG path commands the normalizer handles directly. Each
 * command is followed by one or more (x, y) pairs in absolute or relative
 * coordinates. `Z`/`z` carry no coordinates and pass through.
 *
 * Anything outside this set (H, h, V, v, T, t, S, s, A, a) falls back to
 * the path's getBBox rectangle — safer than half-parsing a mis-coord that
 * would render at the wrong scale. Real Canva clip-paths in the wild use
 * only M/L/Q/C/Z, so the fallback is rare.
 */
const SAFE_PATH_COMMANDS = new Set(['M', 'm', 'L', 'l', 'Q', 'q', 'C', 'c', 'Z', 'z']);
const PAIRS_PER_COMMAND: Record<string, number> = {
  M: 1, m: 1,
  L: 1, l: 1,
  Q: 2, q: 2,
  C: 3, c: 3,
};

/**
 * Normalize an SVG path `d=` string from viewport coords to 0..1 of the
 * slot bbox.
 *
 * Steps per (x, y) pair:
 *   1. If `ctm` is non-identity, transform (x, y) through it first.
 *   2. For ABSOLUTE commands (M, L, Q, C): subtract slot origin, divide
 *      by slot size → 0..1 inside the slot.
 *   3. For RELATIVE commands (m, l, q, c): scale the delta by 1/slotW
 *      and 1/slotH. No origin shift — deltas are translation-invariant.
 *
 * If the path contains any command outside SAFE_PATH_COMMANDS, falls back
 * to a normalized rectangle covering the path's bbox (via `cpEl.getBBox`).
 *
 * Exported for unit testing.
 */
export function normalizeClipPathToSlot(
  d: string,
  ctm: DOMMatrix | null,
  slotX: number,
  slotY: number,
  slotW: number,
  slotH: number,
  cpEl?: SVGGraphicsElement,
): string | null {
  if (slotW <= 0 || slotH <= 0) return null;

  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
  if (!tokens) return null;

  // Pre-scan for unsupported commands.
  for (const t of tokens) {
    if (/^[a-zA-Z]$/.test(t) && !SAFE_PATH_COMMANDS.has(t)) {
      console.warn(
        `templatePipeline: unsupported clip-path command "${t}" — falling back to bbox rect`
      );
      return fallbackToBboxRect(cpEl, ctm, slotX, slotY, slotW, slotH);
    }
  }

  // Helper: apply CTM to a viewport-space point, then normalize.
  const transformAbs = (px: number, py: number): [number, number] => {
    let tx = px;
    let ty = py;
    if (ctm) {
      tx = ctm.a * px + ctm.c * py + ctm.e;
      ty = ctm.b * px + ctm.d * py + ctm.f;
    }
    return [(tx - slotX) / slotW, (ty - slotY) / slotH];
  };

  // For relative deltas: scale by the CTM's linear part (no translation),
  // then divide by slot size.
  const transformDelta = (dx: number, dy: number): [number, number] => {
    let tx = dx;
    let ty = dy;
    if (ctm) {
      tx = ctm.a * dx + ctm.c * dy;
      ty = ctm.b * dx + ctm.d * dy;
    }
    return [tx / slotW, ty / slotH];
  };

  const out: string[] = [];
  let cmd = '';
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (/^[a-zA-Z]$/.test(t)) {
      cmd = t;
      out.push(t);
      i++;
      continue;
    }
    if (cmd === 'Z' || cmd === 'z') {
      // Shouldn't see numeric operands under Z; skip defensively.
      i++;
      continue;
    }
    const n = PAIRS_PER_COMMAND[cmd];
    if (n === undefined) {
      // Defensive: caught by the pre-scan, but bail just in case.
      return fallbackToBboxRect(cpEl, ctm, slotX, slotY, slotW, slotH);
    }
    const isRelative = cmd === cmd.toLowerCase();
    for (let p = 0; p < n; p++) {
      const px = parseFloat(tokens[i]);
      const py = parseFloat(tokens[i + 1]);
      const [nx, ny] = isRelative ? transformDelta(px, py) : transformAbs(px, py);
      out.push(formatNum(nx), formatNum(ny));
      i += 2;
    }
  }
  return out.join(' ').replace(/\s+/g, ' ').trim();
}

function formatNum(n: number): string {
  // Strip trailing zeros without losing precision; keep `0` as `0`.
  if (!Number.isFinite(n)) return '0';
  // toFixed(6) handles negative-zero and tiny FP residue; parseFloat strips trailing zeros.
  return String(parseFloat(n.toFixed(6)));
}

/**
 * Last-resort fallback: when the clip-path uses commands we don't
 * confidently parse, return a normalized rectangle covering the
 * element's bbox. Worse than the real shape but never wrong-scale.
 */
function fallbackToBboxRect(
  cpEl: SVGGraphicsElement | undefined,
  ctm: DOMMatrix | null,
  slotX: number,
  slotY: number,
  slotW: number,
  slotH: number,
): string | null {
  if (!cpEl || typeof cpEl.getBBox !== 'function') return null;
  let bbox: { x: number; y: number; width: number; height: number };
  try {
    bbox = cpEl.getBBox();
  } catch {
    return null;
  }
  if (!bbox || bbox.width <= 0 || bbox.height <= 0) return null;
  // Transform the four bbox corners through cpCtm if present.
  const cornersVp = [
    { x: bbox.x, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
    { x: bbox.x, y: bbox.y + bbox.height },
  ].map((p) =>
    ctm
      ? { x: ctm.a * p.x + ctm.c * p.y + ctm.e, y: ctm.b * p.x + ctm.d * p.y + ctm.f }
      : p
  );
  const minVx = Math.min(...cornersVp.map((c) => c.x));
  const maxVx = Math.max(...cornersVp.map((c) => c.x));
  const minVy = Math.min(...cornersVp.map((c) => c.y));
  const maxVy = Math.max(...cornersVp.map((c) => c.y));
  const x0 = (minVx - slotX) / slotW;
  const y0 = (minVy - slotY) / slotH;
  const x1 = (maxVx - slotX) / slotW;
  const y1 = (maxVy - slotY) / slotH;
  return `M${formatNum(x0)},${formatNum(y0)} L${formatNum(x1)},${formatNum(y0)} L${formatNum(x1)},${formatNum(y1)} L${formatNum(x0)},${formatNum(y1)} Z`;
}

async function rasterizeImageElement(img: SVGImageElement, maxEdge: number): Promise<string> {
  const href = img.getAttribute('xlink:href') ?? img.getAttribute('href') ?? '';
  const imgEl = new Image();
  imgEl.crossOrigin = 'anonymous';
  imgEl.src = href;
  await new Promise<void>((resolve, reject) => {
    imgEl.onload = () => resolve();
    imgEl.onerror = () => reject(new Error(`rasterize: failed to load ${href.slice(0, 60)}…`));
  });

  const scale = Math.min(1, maxEdge / Math.max(imgEl.naturalWidth, imgEl.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(imgEl.naturalWidth * scale);
  canvas.height = Math.round(imgEl.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('rasterize: no 2d context');
  ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.7);
}

export interface PhotoFit {
  scale: number;
  offsetX: number;
  offsetY: number;
}

function isDefaultFit(fit: PhotoFit | undefined): boolean {
  if (!fit) return true;
  return fit.scale === 1 && fit.offsetX === 0 && fit.offsetY === 0;
}

function buildFitTransform(fit: PhotoFit, imgWidth: number, imgHeight: number): string {
  const tx = (fit.offsetX / 100) * imgWidth;
  const ty = (fit.offsetY / 100) * imgHeight;
  return `translate(${tx} ${ty}) scale(${fit.scale})`;
}

/**
 * Browser-side SVG photo substitution. Same shape as the worker-side
 * applyPhotosToTemplateSvg in worker/src/lib/templateSvgApply.ts (which
 * uses linkedom). Native DOMParser/XMLSerializer here — no new deps.
 *
 * For each slot with a URL: set href, remove xlink:href, set
 * preserveAspectRatio="xMidYMid slice" (cover-fit), apply pan/zoom
 * transform when non-default.
 *
 * For each slot WITHOUT a URL: set opacity=0 (placeholder hidden,
 * surrounding decoration preserved).
 */
export function applyPhotosToTemplateSvg(
  svgString: string,
  photoUrlsBySlot: Record<number, string>,
  photoFitsBySlot: Record<number, PhotoFit>,
  slots: Array<{ dom_index: number }>,
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const images = Array.from(doc.querySelectorAll('image'));

  for (const slot of slots) {
    const el = images[slot.dom_index];
    if (!el) continue;

    const url = photoUrlsBySlot[slot.dom_index];
    if (url) {
      el.setAttribute('href', url);
      el.removeAttribute('xlink:href');
      el.setAttribute('preserveAspectRatio', 'xMidYMid slice');

      const fit = photoFitsBySlot[slot.dom_index];
      if (!isDefaultFit(fit) && fit) {
        const w = Number(el.getAttribute('width') ?? '0');
        const h = Number(el.getAttribute('height') ?? '0');
        el.setAttribute('transform', buildFitTransform(fit, w, h));
      } else {
        el.removeAttribute('transform');
      }
    } else {
      el.setAttribute('opacity', '0');
    }
  }

  return new XMLSerializer().serializeToString(doc);
}
